import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { getAuthUrl } from "@/utils/redirectUtils";
import {
  ArrowRight,
  Play,
  DollarSign,
  Eye,
  Target,
  Search,
  Filter,
  BarChart3,
  Layers,
  Rocket,
  X,
  Check,
  Sparkles,
  TrendingUp,
  Zap,
} from "lucide-react";

const KeywordOptimizacionES = () => {
  const problems = [
    {
      icon: <DollarSign className="h-8 w-8" />,
      title: "Agotamiento del presupuesto",
      description:
        "Tu presupuesto diario se consume con 1-2 clics en palabras clave caras y competitivas. A media manana, tus anuncios dejan de mostrarse por completo.",
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Cero visibilidad",
      description:
        "Con el presupuesto agotado antes del almuerzo, tu negocio es invisible durante las 14+ horas restantes del dia. Los clientes potenciales nunca ven tus anuncios.",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Baja relevancia de anuncios",
      description:
        "Las palabras clave genericas y caras atraen buscadores amplios que no estan listos para convertir. Pagas precios premium por trafico de baja intencion.",
    },
  ];

  const pipelineSteps = [
    {
      icon: <Search className="h-6 w-6" />,
      title: "Analisis de Presupuesto y CPA",
      description:
        "Analizamos tu presupuesto diario y CPA objetivo contra las estimaciones de puja de cada palabra clave.",
    },
    {
      icon: <Filter className="h-6 w-6" />,
      title: "Filtro de Asequibilidad",
      description:
        "Eliminamos automaticamente las palabras clave que exceden tu presupuesto, manteniendo solo las que puedes pagar.",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Priorizacion por Volumen de Busqueda",
      description:
        "Ordenamos las palabras clave asequibles por volumen de busqueda para maximizar tu alcance.",
    },
    {
      icon: <Layers className="h-6 w-6" />,
      title: "Agrupacion Tematica",
      description:
        "Organizamos las palabras clave en grupos tematicos coherentes para mejorar la relevancia de tus anuncios.",
    },
    {
      icon: <Rocket className="h-6 w-6" />,
      title: "Grupos de Anuncios Listos para Campana",
      description:
        "Entregamos grupos de anuncios optimizados y listos para activarse en Google Ads inmediatamente.",
    },
  ];

  const comparisonWithout = [
    { metric: "Palabras clave activas", value: "2" },
    { metric: "CPC promedio", value: "$42.50" },
    { metric: "Estado del presupuesto", value: "Agotado a las 10:15" },
    { metric: "Clics diarios", value: "1-2" },
    { metric: "Relevancia del anuncio", value: "Baja" },
  ];

  const comparisonWith = [
    { metric: "Palabras clave activas", value: "89+" },
    { metric: "CPC promedio", value: "$5.80" },
    { metric: "Estado del presupuesto", value: "Activo todo el dia" },
    { metric: "Clics diarios", value: "45+" },
    { metric: "Relevancia del anuncio", value: "Alta" },
  ];

  const stats = [
    { value: "+340%", label: "Mas palabras clave activas" },
    { value: "-62%", label: "Menos gasto desperdiciado" },
    { value: "+85%", label: "Mayor relevancia" },
    { value: "3.2x", label: "Mas impresiones diarias" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Optimizacion de Palabras Clave | Google Ads Inteligente | Upfixe</title>
        <meta
          name="description"
          content="Deja de malgastar tu presupuesto de Google Ads en palabras clave que no puedes pagar. Upfixe filtra por CPA, prioriza por volumen de busqueda y organiza en grupos de anuncios tematicos."
        />
        <meta
          name="keywords"
          content="optimizacion palabras clave, estrategia palabras clave google ads, seleccion palabras clave presupuesto, optimizacion presupuesto google ads, grupos anuncios tematicos"
        />
        <link rel="canonical" href="https://upfixe.com/es/optimizacion-palabras-clave" />
      </Helmet>

      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-32 pb-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-primary/5" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/3 rounded-full blur-3xl" />

          <div className="container mx-auto max-w-5xl relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge className="px-4 py-1.5 text-sm bg-primary/10 text-primary border-primary/20 mb-6">
                Seleccion Inteligente de Palabras Clave
              </Badge>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Deja de malgastar tu presupuesto en palabras clave{" "}
              <span className="text-primary">que no puedes pagar</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Upfixe compara tu presupuesto diario y CPA objetivo contra la estimacion de puja de cada
              palabra clave, filtra las que puedes pagar, prioriza por volumen de busqueda y las organiza
              en grupos de anuncios tematicos — para que tus anuncios sean efectivos en lugar de quemar
              todo en un solo clic caro.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="h-14 px-8 text-lg"
                onClick={() => (window.location.href = getAuthUrl())}
              >
                Optimizar Mis Palabras Clave <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-14 px-8 text-lg" asChild>
                <Link to="/how-it-works">
                  <Play className="mr-2 h-5 w-5" /> Ver Como Funciona
                </Link>
              </Button>
            </motion.div>

            <motion.p
              className="text-sm text-muted-foreground mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              Sin tarjeta de credito. Campana gratuita incluida.
            </motion.p>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <Badge className="px-3 py-1 text-sm bg-red-100 text-red-700 border-red-200 mb-4">
                  El Problema
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  La mayoria de los anunciantes agotan su presupuesto antes de las 10 AM
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  El enfoque predeterminado de Google Ads es pujar por palabras clave competitivas de alto
                  volumen. Pero cuando tu presupuesto diario es de $30-$100, un solo clic de $40 te deja
                  sin nada. Tus campanas se apagan mientras los competidores con mayores presupuestos
                  permanecen visibles todo el dia.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {problems.map((problem, index) => (
                <motion.div
                  key={problem.title}
                  className="bg-white rounded-xl p-6 border border-border hover:border-red-200 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 rounded-lg bg-red-100 text-red-600 flex items-center justify-center mb-4">
                    {problem.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{problem.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{problem.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pipeline Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  Nuestro Proceso
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Pipeline de 5 pasos para palabras clave inteligentes
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Cada palabra clave pasa por un proceso riguroso para garantizar que sea asequible,
                  relevante y este lista para convertir.
                </p>
              </motion.div>
            </div>

            <div className="space-y-6">
              {pipelineSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  className="flex items-start gap-5 bg-white rounded-xl p-6 border border-border shadow-sm"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-lg font-bold shadow-lg shadow-primary/25">
                    {index + 1}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                        {step.icon}
                      </div>
                      <h3 className="text-lg font-bold">{step.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  Comparacion
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  La diferencia es de noche y dia
                </h2>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Sin Upfixe */}
              <motion.div
                className="rounded-2xl border-2 border-red-200 overflow-hidden"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-red-50 px-6 py-4 border-b border-red-200">
                  <div className="flex items-center gap-2">
                    <X className="h-6 w-6 text-red-500" />
                    <h3 className="text-lg font-bold text-red-800">Sin Upfixe</h3>
                  </div>
                  <p className="text-sm text-red-600 mt-1">Seleccion manual de palabras clave</p>
                </div>
                <div className="divide-y divide-red-100">
                  {comparisonWithout.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="px-6 py-4 flex justify-between items-center"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <span className="text-sm text-muted-foreground">{item.metric}</span>
                      <span className="text-sm font-bold text-red-700">{item.value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Con Upfixe */}
              <motion.div
                className="rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg shadow-green-100/50"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-green-50 px-6 py-4 border-b border-green-200">
                  <div className="flex items-center gap-2">
                    <Check className="h-6 w-6 text-green-600" />
                    <h3 className="text-lg font-bold text-green-800">Con Upfixe</h3>
                  </div>
                  <p className="text-sm text-green-600 mt-1">Optimizacion inteligente de palabras clave</p>
                </div>
                <div className="divide-y divide-green-100">
                  {comparisonWith.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="px-6 py-4 flex justify-between items-center"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.08 }}
                    >
                      <span className="text-sm text-muted-foreground">{item.metric}</span>
                      <span className="text-sm font-bold text-green-700">{item.value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Results / Stats Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  El Impacto
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Lo que ofrecen las palabras clave inteligentes
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Cuando cada palabra clave es asequible y relevante, toda tu campana rinde mejor.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="bg-white rounded-xl p-6 border border-border text-center shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <span className="text-4xl md:text-5xl font-bold text-primary">{stat.value}</span>
                  <p className="text-sm text-muted-foreground mt-2 font-medium">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Listo para que cada dolar cuente?
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
                Deja de adivinar que palabras clave elegir. Deja que Upfixe encuentre automaticamente las
                palabras clave que tu presupuesto puede manejar, las priorice por volumen de busqueda y las
                organice en grupos de anuncios de alto rendimiento.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  className="h-14 px-10 text-lg"
                  onClick={() => (window.location.href = getAuthUrl())}
                >
                  Iniciar Campana Gratis <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="h-14 px-10 text-lg" asChild>
                  <Link to="/pricing">Ver Precios</Link>
                </Button>
              </div>

              <p className="text-sm text-muted-foreground mb-12">
                Sin tarjeta de credito. Tu primera campana es gratis.
              </p>

              <div className="flex flex-wrap gap-4 justify-center text-sm">
                <Link to="/features" className="text-primary hover:underline">
                  Todas las Funciones
                </Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/how-it-works" className="text-primary hover:underline">
                  Como Funciona
                </Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/pricing" className="text-primary hover:underline">
                  Planes y Precios
                </Link>
                <span className="text-muted-foreground">|</span>
                <Link to="/faqs" className="text-primary hover:underline">
                  Preguntas Frecuentes
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default KeywordOptimizacionES;
